import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const VideoSection = () => {
  const navigate = useNavigate();

  const stats = [
    { num: "0.50%", label: "Flat fee at closing" },
    { num: "$22k+", label: "Average saved" },
    { num: "100%", label: "Concierge guided" },
    { num: "$0", label: "Agent commission" },
  ];

  return (
    <section className="relative bg-canvas py-16 md:py-20 px-8 md:px-12 overflow-hidden">
      {/* ambient warmth */}
      <div className="pointer-events-none absolute -top-32 right-0 w-[500px] h-[500px] rounded-full bg-gold/8 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 left-0 w-[450px] h-[450px] rounded-full bg-wood/10 blur-3xl" />

      <div className="relative max-w-[960px] mx-auto">
        {/* Full-width copy */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true, margin: "-10%" }}
          className="max-w-2xl"
        >
          <div className="inline-flex items-center gap-2.5 mb-6">
            <span className="w-8 h-px bg-gold" />
            <span className="text-[11px] font-medium tracking-[0.25em] uppercase text-ink-soft">
              See it in motion
            </span>
          </div>
          <h2 className="font-display text-5xl md:text-7xl font-light text-ink leading-[1.0] tracking-tight mb-6">
            Watch how it
            <br />
            <span className="accent-italic text-wood-deep">actually works.</span>
          </h2>
          <p className="text-base md:text-lg text-ink-soft leading-[1.7] font-light max-w-xl">
            No gimmicks. No hidden fees. A short tour through listing, showings, offers, and closing — guided every step of the way.
          </p>
        </motion.div>

        {/* Editorial stats — hairline divided */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true, margin: "-10%" }}
          className="mt-14 pt-8 border-t border-ink/10 grid grid-cols-2 md:grid-cols-4 gap-8"
        >
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              viewport={{ once: true }}
            >
              <div className="font-display text-4xl md:text-5xl font-light text-ink tabular-nums leading-none mb-2">
                {stat.num}
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-ink-soft font-medium">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true, margin: "-10%" }}
          className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5 pt-8 border-t border-ink/10"
        >
          <p className="font-display text-2xl md:text-3xl font-light text-ink tracking-tight max-w-md">
            Ready to keep <span className="accent-italic text-wood-deep">what's yours?</span>
          </p>
          <div className="flex flex-wrap gap-3 sm:ml-auto">
            <Button
              size="lg"
              onClick={() => navigate("/auth")}
              className="bg-gold hover:bg-gold-deep text-ink font-medium px-7 py-6 text-sm tracking-wide rounded-md shadow-soft hover:shadow-lift transition-all duration-500"
            >
              Join REALV3
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate("/about")}
              className="border border-ink/15 bg-transparent hover:bg-ink hover:text-canvas hover:border-ink text-ink font-medium px-7 py-6 text-sm tracking-wide rounded-md transition-all duration-500"
            >
              Learn more
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default VideoSection;
